








































































































































import { defineComponent, ref, watch, computed, Ref, onMounted, onBeforeUnmount } from '@vue/composition-api';
import {
    PageHeader,
    PageBody,
    Tooltip,
    InfoCircleSmallIcon,
} from '@nimiq/vue-components';
import { parseRequestLink, Currency, CurrencyInfo } from '@nimiq/utils';
import { useRouter, RouteName } from '@/router';
import { useI18n } from '@/lib/useI18n';
import { nextTick } from '@/lib/nextTick';
import Modal, { disableNextModalTransition } from './Modal.vue';
import BtcAddressInput from '../BtcAddressInput.vue';
import BtcLabelInput from '../BtcLabelInput.vue';
import AmountInput from '../AmountInput.vue';
import AmountMenu from '../AmountMenu.vue';
import FeeSelector from '../FeeSelector.vue';
import FiatConvertedAmount from '../FiatConvertedAmount.vue';
import SendModalFooter from '../SendModalFooter.vue';
import StatusScreen, { State, SUCCESS_REDIRECT_DELAY } from '../StatusScreen.vue';
import { useAccountStore } from '../../stores/Account';
import { useBtcAddressStore } from '../../stores/BtcAddress';
import { useBtcLabelsStore } from '../../stores/BtcLabels';
import { useBtcNetworkStore } from '../../stores/BtcNetwork';
import { useFiatStore } from '../../stores/Fiat';
import { useSettingsStore } from '../../stores/Settings';
import { CryptoCurrency, FiatCurrency, FIAT_CURRENCIES_OFFERED } from '../../lib/Constants';
import { sendBtcTransaction } from '../../hub';
import { useWindowSize } from '../../composables/useWindowSize';
import { loadBitcoinJS } from '../../lib/BitcoinJSLoader';
import { selectOutputs, estimateFees, normalizeAddress, validateAddress } from '../../lib/BitcoinTransactionUtils';
import { getElectrumClient } from '../../electrum';
import DoubleInput from '../DoubleInput.vue';

export enum RecipientType {
    NEW_CONTACT,
    KNOWN_CONTACT,
    GLOBAL_ADDRESS,
}

export default defineComponent({
    name: 'btc-send-modal',
    props: {
        requestUri: {
            type: String,
            required: false,
        },
    },
    setup(props) {
        const { $t } = useI18n();
        const {
            state: addresses$,
            accountUtxos,
            accountBalance,
        } = useBtcAddressStore();
        const {
            setRecipientLabel,
            getRecipientLabel,
        } = useBtcLabelsStore();
        const { state: network$, isFetchingTxHistory } = useBtcNetworkStore();

        const recipientWithLabel = ref<{address: string, label: string, type: RecipientType} | null>(null);

        function saveRecipientLabel() {
            if (!recipientWithLabel.value || recipientWithLabel.value.type !== RecipientType.NEW_CONTACT) return;
            setRecipientLabel(recipientWithLabel.value.address, recipientWithLabel.value.label);
        }

        function resetRecipient() {
            if (gotRequestUriRecipient.value) return;
            recipientWithLabel.value = null;
        }

        function onAddressEntered(address: string) {
            if (recipientWithLabel.value && recipientWithLabel.value.address === address) return;

            // Find label across recipient labels, own addresses
            let label = '';
            let type = RecipientType.NEW_CONTACT; // Can be stored as a new contact by default
            // Search other stored addresses
            const ownedAddressInfo = addresses$.addressInfos[address];
            if (ownedAddressInfo) {
                // Find account label
                const { accountInfos } = useAccountStore();
                label = Object.values(accountInfos.value)
                    .find((accountInfo) => accountInfo.btcAddresses.external.includes(address))?.label
                    || Object.values(accountInfos.value)
                        .find((accountInfo) => accountInfo.btcAddresses.internal.includes(address))!.label;
                type = RecipientType.NEW_CONTACT; // Allow overwriting suggested account label
            }
            // Search recipient labels
            if (getRecipientLabel.value(address)) {
                label = getRecipientLabel.value(address)!;
                type = RecipientType.KNOWN_CONTACT; // Show warning and disable input
            }
            // // Search global address book
            // const globalLabel = AddressBook.getLabel(address);
            // if (globalLabel) {
            //     label = globalLabel;
            //     type = RecipientType.GLOBAL_ADDRESS;
            // }

            recipientWithLabel.value = { address, label, type };
        }

        function onDomainEntered(domain: string, address: string) {
            recipientWithLabel.value = {
                address,
                label: domain,
                type: RecipientType.NEW_CONTACT,
            };
            addressInputValue.value = address;
        }

        const amount = ref(0);
        const feePerByte = ref(1);

        const requiredInputs = computed(() => selectOutputs(accountUtxos.value, amount.value, feePerByte.value));

        const fee = computed(() => estimateFees(
            requiredInputs.value.utxos.length || 1,
            requiredInputs.value.changeAmount > 0 ? 2 : 1,
            feePerByte.value,
        ));

        const feeForSendingAll = computed(() => estimateFees(accountUtxos.value.length, 1, feePerByte.value));

        const maxSendableAmount = computed(() => Math.max((accountBalance.value || 0) - feeForSendingAll.value, 0));

        const amountMenuOpened = ref(false);

        const feeOptions = ref([] as number[]);

        let isFetchingFeeEstimates = false;
        async function fetchFeeEstimates() {
            if (isFetchingFeeEstimates) return;
            isFetchingFeeEstimates = true;

            const client = await getElectrumClient();
            await client.waitForConsensusEstablished();

            // 25 blocks is the maximum that some ElectrumX servers estimate for
            const fees = await client.estimateFees([25, 12, 1]);
            console.debug('Fee Estimates:', fees); // eslint-disable-line no-console

            const feesByTarget: number[] = [];
            feesByTarget[1] = fees[1] || 3;
            feesByTarget[12] = fees[12] || 2;
            feesByTarget[25] = fees[25] || 1;

            feeOptions.value = feesByTarget;
            isFetchingFeeEstimates = false;
        }
        fetchFeeEstimates();

        const feeEstimatesInterval = setInterval(fetchFeeEstimates, 60 * 1000); // Update every 60 seconds
        let successCloseTimeout = 0;

        onBeforeUnmount(() => {
            clearInterval(feeEstimatesInterval);
            window.clearTimeout(successCloseTimeout);
        });

        const activeCurrency = ref<CryptoCurrency | FiatCurrency>(CryptoCurrency.BTC);
        const fiatAmount = ref(0);

        function onCurrencySelected(currency: CryptoCurrency | FiatCurrency) {
            activeCurrency.value = currency;
        }

        const { state: fiat$, exchangeRates, currency: referenceCurrency } = useFiatStore();
        const otherFiatCurrencies = computed(() => FIAT_CURRENCIES_OFFERED.filter((fiat) => fiat !== fiat$.currency));

        const fiatCurrencyInfo = computed(() => {
            if (activeCurrency.value === CryptoCurrency.BTC) {
                return new CurrencyInfo(referenceCurrency.value);
            }
            return new CurrencyInfo(activeCurrency.value);
        });

        const fiatToBtcDecimalRatio = computed(() => 10 ** fiatCurrencyInfo.value.decimals / 1e8);

        watch(activeCurrency, (currency) => {
            if (currency === CryptoCurrency.BTC) {
                fiatAmount.value = 0;
                return;
            }

            // Fiat store already has all exchange rates for all supported fiat currencies
            // TODO: What to do when exchange rates are not yet populated?
            fiatAmount.value = amount.value * fiat$.exchangeRates.btc[currency]! * fiatToBtcDecimalRatio.value;
        });

        watch(() => {
            if (activeCurrency.value === CryptoCurrency.BTC || gotRequestUriAmount.value) return;
            amount.value = Math.floor(
                fiatAmount.value
                / exchangeRates.value.btc[activeCurrency.value]!
                / fiatToBtcDecimalRatio.value);
        });

        async function sendMax() {
            if (gotRequestUriAmount.value) return;
            if (activeCurrency.value !== CryptoCurrency.BTC) {
                fiatAmount.value = maxSendableAmount.value
                    * fiat$.exchangeRates.btc[activeCurrency.value]!
                    * fiatToBtcDecimalRatio.value;
            }
            // Need to wait here for the next processing tick, as otherwise we would have a
            // race condition between the amount assignment and the fiatAmount watcher.
            await nextTick();
            amount.value = maxSendableAmount.value;
        }

        function updateFee(newFeePerByte: number) {
            const isSendingMax = amount.value === maxSendableAmount.value;
            feePerByte.value = newFeePerByte;
            if (isSendingMax) sendMax();
        }

        const canSend = computed(() =>
            network$.consensus === 'established'
            && !!network$.height
            && !!recipientWithLabel.value
            && !!recipientWithLabel.value.address
            && !isFetchingTxHistory.value
            && !!amount.value
            && amount.value <= maxSendableAmount.value,
        );

        const addressInputValue = ref(''); // Used for setting the address from a request URI or unstoppable domain

        const gotRequestUriRecipient = ref(false);
        const gotRequestUriAmount = ref(false);
        async function parseRequestUri(uri: string, event?: ClipboardEvent) {
            // We don't validate addresses here yet because it requires asynchronously loading BitcoinJS, and
            // event.preventDefault() needs to be called synchronously below. Instead, the validation happens afterward.
            // The normalization does not require BitcoinJS to be loaded.
            const parsedRequestLink = parseRequestLink(uri, {
                currencies: [Currency.BTC],
                normalizeAddress: {
                    [Currency.BTC]: normalizeAddress,
                },
            });
            if (!parsedRequestLink) return;

            if (event) {
                event.stopPropagation(); // Prevent pasting
            }

            await loadBitcoinJS();
            if (!validateAddress(parsedRequestLink.recipient)) return;

            if (parsedRequestLink.amount) {
                gotRequestUriAmount.value = true;
                amount.value = parsedRequestLink.amount;
            }

            gotRequestUriRecipient.value = true;
            addressInputValue.value = parsedRequestLink.recipient;
            // Wait for BtcAddressInput to trigger onAddressEntered which sets recipientWithLabel.
            await new Promise<void>((resolve) => {
                const unwatch = watch(() => {
                    if (recipientWithLabel.value?.address !== parsedRequestLink.recipient) return;
                    resolve();
                    unwatch();
                });
            });
            if (!recipientWithLabel.value!.label && parsedRequestLink.label) {
                recipientWithLabel.value!.label = parsedRequestLink.label;
            }
        }

        if (props.requestUri) {
            parseRequestUri(props.requestUri);
        }

        /**
         * Autofocus
         */

        const addressInput$ = ref<BtcAddressInput>(null);
        const labelInput$ = ref<BtcLabelInput>(null);

        const { isMobile } = useWindowSize();

        async function focus(element$: Ref<BtcAddressInput | BtcLabelInput | null>) {
            // TODO: Detect onscreen keyboards instead?
            if (isMobile.value) return;

            await nextTick();
            if (!element$.value) return;
            element$.value.focus();
        }

        onMounted(async () => {
            if (addressInputValue.value) {
                // If an initial address was set via request link, focus the label input instead of the address input,
                // once the request link populated and the label input showed up.
                await new Promise<void>((resolve) => {
                    const unwatch = watch(() => {
                        if (!labelInput$.value) return;
                        resolve();
                        unwatch();
                    });
                });
                focus(labelInput$);
            } else {
                focus(addressInput$);
            }
        });

        /**
         * Status Screen
         */
        const statusScreenOpened = ref(false);
        const statusState = ref<State>(State.LOADING);
        const statusTitle = ref('');
        const statusMessage = ref('');
        const modal$ = ref<Modal>(null);

        async function sign() {
            if (!canSend.value) return;

            // Show loading screen
            statusScreenOpened.value = true;
            statusState.value = State.LOADING;
            statusTitle.value = $t('Sending Transaction') as string;
            statusMessage.value = '';

            let changeAddress: string;
            if (requiredInputs.value.changeAmount > 0) {
                const { nextChangeAddress } = useBtcAddressStore();
                if (!nextChangeAddress.value) {
                    // FIXME: If no unused change address is found, need to request new ones from Hub!
                    throw new Error('No more unused change addresses)');
                }
                changeAddress = nextChangeAddress.value;
            }

            try {
                const plainTx = await sendBtcTransaction({
                    accountId: useAccountStore().state.activeAccountId!,
                    inputs: requiredInputs.value.utxos.map((utxo) => ({
                        address: utxo.address,
                        transactionHash: utxo.transactionHash,
                        outputIndex: utxo.index,
                        outputScript: utxo.witness.script,
                        value: utxo.witness.value,
                    })),
                    output: {
                        address: recipientWithLabel.value!.address,
                        label: recipientWithLabel.value!.label,
                        value: amount.value,
                    },
                    ...(requiredInputs.value.changeAmount > 0 ? {
                        changeOutput: {
                            address: changeAddress!,
                            value: requiredInputs.value.changeAmount,
                        },
                    } : {}),
                });

                if (!plainTx) {
                    statusScreenOpened.value = false;
                    return;
                }

                saveRecipientLabel();

                // Show success screen
                statusState.value = State.SUCCESS;
                statusTitle.value = recipientWithLabel.value!.label
                    ? $t('Sent {btc} BTC to {name}', {
                        btc: amount.value / 1e8,
                        name: recipientWithLabel.value!.label,
                    }) as string
                    : $t('Sent {btc} BTC', {
                        btc: amount.value / 1e8,
                    }) as string;

                // Close modal
                successCloseTimeout = window.setTimeout(() => {
                    if (statusState.value !== State.SUCCESS) return;
                    modal$.value!.forceClose();
                }, SUCCESS_REDIRECT_DELAY);
            } catch (error) {
                // console.debug(error);

                // Show error screen
                statusState.value = State.WARNING;
                statusTitle.value = $t('Something went wrong') as string;
                statusMessage.value = (error as Error).message;
            }
        }

        function onStatusMainAction() {
            sign();
        }

        function onStatusAlternativeAction() {
            statusScreenOpened.value = false;
        }

        function onCloseOverlay() {
            if (statusState.value !== State.WARNING) {
                // Do nothing when the loading or success status overlays are shown as they will be auto-closed.
                return;
            }
            statusScreenOpened.value = false;
        }

        const { btcUnit } = useSettingsStore();
        const router = useRouter();

        function back() {
            disableNextModalTransition();
            router.back();
        }

        return {
            // General
            RecipientType,
            CryptoCurrency,
            FiatCurrency,
            modal$,

            // Recipient Input
            addressInputValue,
            resetRecipient,
            onAddressEntered,
            onDomainEntered,
            recipientWithLabel,
            gotRequestUriRecipient,
            gotRequestUriAmount,
            parseRequestUri,

            // Amount Input
            amount,
            feePerByte,
            fee,
            maxSendableAmount,
            amountMenuOpened,
            feeOptions,
            activeCurrency,
            onCurrencySelected,
            btcUnit,
            fiatAmount,
            fiatCurrencyInfo,
            sendMax,
            updateFee,
            fiatCurrency: fiat$.currency,
            otherFiatCurrencies,
            canSend,
            sign,
            // onboard,

            // DOM refs for autofocus
            addressInput$,
            labelInput$,

            // Status Screen
            statusScreenOpened,
            statusTitle,
            statusState,
            statusMessage,
            onStatusMainAction,
            onStatusAlternativeAction,
            onCloseOverlay,

            back,
            RouteName,
        };
    },
    components: {
        Modal,
        PageHeader,
        PageBody,
        BtcAddressInput,
        BtcLabelInput,
        AmountInput,
        AmountMenu,
        FeeSelector,
        FiatConvertedAmount,
        Tooltip,
        InfoCircleSmallIcon,
        SendModalFooter,
        StatusScreen,
        DoubleInput,
    },
});
